import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { Alert, Input } from '@ibk/shared-ui';
import { useEmployeesData } from '../../../../hooks';
import { HiringStep, SidebarStatus } from '../../../../types/hiring';
import { validateEmployeeCount } from '../../../../utils/employeesValidation';
import { ContractSummarySidebar } from '../ContractSummarySidebar';
import styles from './HiringEmployees.module.css';
export function HiringEmployees({ onComplete, onBack, sidebarSteps, address, employeeCount: propEmployeeCount, productFamilies, stepNumber, headerStepper, }) {
    const { t } = useTranslation();
    const { employeesData, saveEmployeesData } = useEmployeesData();
    const [employeeCount, setEmployeeCount] = useState(employeesData?.employeeCount?.toString() ?? '');
    const [validationErrors, setValidationErrors] = useState([]);
    useEffect(() => {
        if (employeesData?.employeeCount) {
            setEmployeeCount(employeesData.employeeCount.toString());
        }
    }, [employeesData?.employeeCount]);
    const breadcrumbs = [
        { label: t('hiring.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('hiring.breadcrumb.hiring'), isActive: true },
    ];
    const currentStep = {
        id: HiringStep.EMPLOYEES,
        title: t('hiring.step.employees'),
        number: stepNumber ?? 2,
        status: SidebarStatus.ACTIVE,
    };
    const handleEmployeeCountChange = useCallback((event) => {
        const value = event.target.value;
        const numericValue = value.replace(/\D/g, '').slice(0, 4);
        if (numericValue === '') {
            setEmployeeCount('');
        }
        else {
            setEmployeeCount(numericValue);
        }
        if (parseInt(numericValue, 10) > 0) {
            setValidationErrors([]);
        }
    }, []);
    const handleNext = useCallback(() => {
        const employeeCountNumber = parseInt(employeeCount, 10);
        const validation = validateEmployeeCount(employeeCountNumber);
        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            return;
        }
        const data = { employeeCount: employeeCountNumber };
        saveEmployeesData(data);
        onComplete();
    }, [employeeCount, onComplete, saveEmployeesData]);
    useEffect(() => {
        if (employeeCount !== '') {
            const employeeCountNumber = parseInt(employeeCount, 10);
            if (employeeCountNumber === 0) {
                setValidationErrors(['hiring.employees.validation.required']);
            }
            else {
                setValidationErrors([]);
            }
        }
        else {
            setValidationErrors([]);
        }
    }, [employeeCount]);
    const isNextButtonDisabled = () => {
        if (employeeCount === '')
            return true;
        const employeeCountNumber = parseInt(employeeCount, 10);
        return isNaN(employeeCountNumber) || employeeCountNumber < 1 || validationErrors.length > 0;
    };
    const renderMainCard = () => (_jsxs(StepperCard, { currentStep: currentStep, showStepper: !headerStepper, actions: {
            back: {
                label: t('hiring.button.back'),
                onClick: onBack,
            },
            next: {
                label: t('hiring.button.next'),
                onClick: handleNext,
                disabled: isNextButtonDisabled(),
            },
        }, children: [headerStepper, _jsxs("div", { className: styles.stepContent, "data-testid": 'viewHiringEmployeesStepContent', children: [_jsx("div", { className: styles.explanationSection, "data-testid": 'viewHiringEmployeesExplanation', children: _jsx("h3", { className: styles.stepTitle, "data-testid": 'textHiringEmployeesTitle', children: t('hiring.employees.title') }) }), _jsx("div", { className: styles.inputSection, "data-testid": 'viewHiringEmployeesInputSection', children: _jsx(Input, { value: employeeCount, onChange: handleEmployeeCountChange, placeholder: t('hiring.employees.field.placeholder'), type: 'text', inputMode: 'numeric', maxLength: 4, ...(validationErrors.length > 0 && { error: t(validationErrors[0]) }), className: styles.employeeCountInput, dataTestId: 'inputHiringEmployeesCount', size: 'large' }) }), _jsx(Alert, { alertType: 'inline', appearance: 'info', message: t('hiring.employees.info.message'), className: styles.infoAlert, alignItems: 'center', testIdIdentifier: 'alertHiringEmployeesInfo' })] })] }));
    const renderSummaryCard = () => {
        const displayEmployeeCount = employeesData?.employeeCount ?? propEmployeeCount;
        return (_jsx(ContractSummarySidebar, { steps: sidebarSteps, address: address, employeeCount: displayEmployeeCount, productFamilies: productFamilies }));
    };
    if (headerStepper) {
        return (_jsx(ContentLayout, { breadcrumbs: breadcrumbs, showSidebar: false, noMaxWidth: true, "data-testid": 'viewHiringEmployeesContainer', children: renderMainCard() }));
    }
    return (_jsx(ContentLayout, { breadcrumbs: breadcrumbs, sidebar: renderSummaryCard(), "data-testid": 'viewHiringEmployeesContainer', children: renderMainCard() }));
}
